import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  Badge,
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  ToggleButton,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowReset20Regular,
  Color20Regular,
  Copy20Regular,
  Dismiss24Regular,
  Layer20Regular,
  Link20Regular,
  PaintBrush20Regular,
  PanelLeft20Regular,
  Square20Regular,
  TextFont20Regular,
  TextFontSize20Regular,
} from '@fluentui/react-icons'
import { useAuth } from '../context/AuthContext'
import { useToast } from './ToastProvider'
import { InfoHint } from './planning/PlanParts'
import { useOverrides } from '../styleguide/react'
import { contrastOnWhite, parseHex } from '../styleguide/color'
import { LOOK_PARAM, lookAndFeel, themeSnippet } from '../lib/lookAndFeel'
import { bayerBrand } from '../theme'

const ICONS: Record<string, ReactElement> = {
  colour: <Color20Regular aria-hidden />,
  sidebar: <PanelLeft20Regular aria-hidden />,
  corners: <Square20Regular aria-hidden />,
  text: <TextFontSize20Regular aria-hidden />,
  font: <TextFont20Regular aria-hidden />,
  depth: <Layer20Regular aria-hidden />,
}

/** White text on a primary button needs this much contrast (WCAG AA). */
const MIN_CONTRAST = 4.5

const useStyles = makeStyles({
  handle: {
    position: 'fixed',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRight: 'none',
    borderRadius: `${tokens.borderRadiusMedium} 0 0 ${tokens.borderRadiusMedium}`,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    boxShadow: tokens.shadow4,
    cursor: 'pointer',
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase200,
    ':hover': { color: tokens.colorBrandForeground1, background: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  // Vertical text renders a plain space narrow enough to read "Look &feel".
  handleLabel: { writingMode: 'vertical-rl', wordSpacing: '0.3em' },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalL,
  },
  rowLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightSemibold,
  },
  options: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS },
  swatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: tokens.borderRadiusCircular,
  },
  note: { color: tokens.colorNeutralForeground3, marginLeft: tokens.spacingHorizontalXS },
  picker: {
    width: '32px',
    height: '24px',
    padding: 0,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    background: 'none',
    cursor: 'pointer',
  },
  // Three buttons don't fit the small drawer's width: Reset is an icon with a
  // tooltip on the left, the two copies stay labelled on the right.
  footer: { justifyContent: 'space-between' },
  copies: { display: 'flex', gap: tokens.spacingHorizontalS },
})

/**
 * Look & feel, for admins only: a handle on the right edge opens a drawer of
 * ready options per feel (colour, sidebar, corners, text size, font, depth),
 * and the whole app follows as you pick. The page stays usable beside it.
 * Only this tab changes, until it reloads; see lib/lookAndFeel.ts.
 */
export function LookAndFeelDrawer() {
  const styles = useStyles()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  const overrides = useOverrides(lookAndFeel)
  const { showToast } = useToast()
  const location = useLocation()
  const [params] = useSearchParams()
  const shared = params.get(LOOK_PARAM)
  const [open, setOpen] = useState(false)
  const handleRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(false)

  // A shared look, opened by an admin: wear it and show where it came from.
  // Anyone else gets the app as shipped — they have no drawer to undo it with.
  useEffect(() => {
    if (!isAdmin || !shared) return
    const look = lookAndFeel.decode(shared)
    if (!Object.keys(look).length) return
    lookAndFeel.apply(look)
    setOpen(true)
  }, [isAdmin, shared])

  // Signed out, or no longer an admin: back to the app as shipped.
  useEffect(() => {
    if (!isAdmin && Object.keys(lookAndFeel.current()).length) lookAndFeel.apply({})
  }, [isAdmin])

  // Back to the handle when the drawer closes, so the keyboard isn't lost.
  useEffect(() => {
    if (wasOpen.current && !open) handleRef.current?.focus()
    wasOpen.current = open
  }, [open])

  if (!isAdmin) return null

  const changed = Object.keys(overrides).length > 0
  const brand = overrides.brand ?? bayerBrand[80].slice(1)
  const contrast = contrastOnWhite(parseHex(`#${brand}`)!)

  const copy = (text: string, done: string) =>
    navigator.clipboard.writeText(text).then(
      () => showToast({ title: done }),
      () => showToast({ title: "Couldn't copy; the browser blocked it.", intent: 'error' }),
    )
  const link = () =>
    `${window.location.origin}${location.pathname}?${LOOK_PARAM}=${lookAndFeel.encode(overrides)}`

  return (
    <>
      {open ? null : (
        <button
          ref={handleRef}
          type="button"
          className={styles.handle}
          onClick={() => setOpen(true)}
          data-testid="look-and-feel-handle"
        >
          <PaintBrush20Regular aria-hidden />
          <span className={styles.handleLabel}>Look & feel</span>
        </button>
      )}
      <OverlayDrawer
        open={open}
        onOpenChange={(_, data) => setOpen(data.open)}
        position="end"
        modalType="non-modal"
        data-testid="look-and-feel-drawer"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <>
                <InfoHint text="Only you see this, in this tab, until you reload. Copy link shows it to another admin. Copy theme is what goes into theme.ts." />
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                />
              </>
            }
          >
            Look & feel
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody>
          {lookAndFeel.config.dimensions.map((dimension) => {
            const active = lookAndFeel.optionOf(overrides, dimension)
            const labelId = `look-${dimension.id}`
            return (
              <section key={dimension.id} className={styles.row} aria-labelledby={labelId}>
                <div className={styles.rowLabel}>
                  {ICONS[dimension.id]}
                  <span id={labelId}>{dimension.label}</span>
                  {active ? null : (
                    <Badge appearance="tint" color="informative">
                      Custom
                    </Badge>
                  )}
                  {dimension.id === 'colour' && contrast < MIN_CONTRAST ? (
                    <Tooltip
                      relationship="description"
                      content={`White text on buttons needs ${MIN_CONTRAST}:1; this is ${contrast.toFixed(1)}:1.`}
                    >
                      <Badge appearance="tint" color="warning" tabIndex={0}>
                        Low contrast
                      </Badge>
                    </Tooltip>
                  ) : null}
                </div>
                <div className={styles.options}>
                  {dimension.options.map((option) => (
                    <ToggleButton
                      key={option.id}
                      size="small"
                      checked={active?.id === option.id}
                      onClick={() =>
                        lookAndFeel.apply(lookAndFeel.withOption(overrides, dimension, option))
                      }
                      icon={
                        option.swatch ? (
                          <span
                            className={styles.swatch}
                            style={{ background: `#${option.swatch[0]}` }}
                            aria-hidden
                          />
                        ) : undefined
                      }
                    >
                      {option.label}
                      {option.note ? <span className={styles.note}>{option.note}</span> : null}
                    </ToggleButton>
                  ))}
                  {dimension.id === 'colour' ? (
                    <input
                      type="color"
                      className={styles.picker}
                      aria-label="Any colour"
                      value={`#${brand}`}
                      onChange={(e) => lookAndFeel.apply({ ...overrides, brand: e.target.value.slice(1) })}
                    />
                  ) : null}
                </div>
              </section>
            )
          })}
        </DrawerBody>

        <DrawerFooter className={styles.footer}>
          <Tooltip content="Reset" relationship="label">
            <Button
              appearance="subtle"
              icon={<ArrowReset20Regular />}
              disabled={!changed}
              onClick={() => lookAndFeel.apply({})}
            />
          </Tooltip>
          <div className={styles.copies}>
            <Button
              size="small"
              icon={<Link20Regular />}
              disabled={!changed}
              onClick={() => copy(link(), 'Link copied')}
            >
              Copy link
            </Button>
            <Button
              size="small"
              appearance="primary"
              icon={<Copy20Regular />}
              disabled={!changed}
              onClick={() => copy(themeSnippet(overrides), 'Theme copied')}
            >
              Copy theme
            </Button>
          </div>
        </DrawerFooter>
      </OverlayDrawer>
    </>
  )
}
